import React, {
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  memo,
} from "react";
import { FormikProps, useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "@/store";
import actionsHandler from "@/store/actions/actionHandler";
import { useTranslation } from "react-i18next";
import BackIcon from "@styles/all-images/icons/back-button.svg";
import { TextField } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PhoneVerification, { OTPPayload } from "../PhoneVerification";
import {
  setTempEmail,
  setTempToken,
  setOtpSent,
} from "@/store/slices/authSlice";
import { FormikErrors } from "formik";
import auditLoggingHandler from "@/composables/updatedChangesHandler";
import CustomLoader from "@/components/common/loader/SpinnerLoader";
import { UserTierType } from "@/constants/enums/user-tier-type.enum";
import RenderOtherDetails from "./RenderOtherDetails";
import DynamicAutocomplete from "@/components/common/inputs/select/DynamicAutocomplete";

export interface CompanyDetails {
  id: number;
  name: string;
  trading_as: string;
  commodity_types: number[];
  administrative_first_name: string;
  administrative_last_name: string;
  administrative_email: string;
  administrative_contact_number: string;
  designation: string;
  facility_required: boolean;
  facility_amount: number;
  debtor_type: {
    debtor_type: string;
    id: number;
  };
  payment_terms: {
    payment_terms: string;
    id: number;
  };
  client_type: {
    client_type: string;
    id: number;
  };
  profession: number;
  reason_to_access: string;
  custom_payment_days?: string;
  credit_facility?: string;
  administrative_profiles: number[];
}

export interface UpdateValues {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
  companies: CompanyDetails[];
}

export interface CommodityType {
  id: number;
  title: string;
}

interface MasterProfile {
  id: number;
  name: string;
}

interface EditMyAccountProps {
  hideSubmitButton?: boolean;
  hideDefaultHeader?: boolean;
  customSubmitHandler?: () => void;
  className?: string;
  userDataAvailableCallback?: () => void;
  isRequest?: boolean;
}

export interface EditMyAccountRef {
  formik: FormikProps<UpdateValues>;
}

const EditMyAccount = memo(
  forwardRef<EditMyAccountRef, EditMyAccountProps>(
    (
      {
        hideSubmitButton = false,
        hideDefaultHeader = false,
        customSubmitHandler,
        className = "",
        userDataAvailableCallback,
        isRequest = true,
      },
      ref,
    ) => {
      const dispatch = useDispatch<AppDispatch>();
      const { t } = useTranslation();
      const userId = JSON.parse(localStorage.getItem("user") || "{}")?.id;
      const navigate = useNavigate();
      const [isLoading, setIsLoading] = useState(false);
      const [showOTPScreen, setShowOTPScreen] = useState(false);
      const { authenticatedCall: fetchUserDetails } =
        actionsHandler("userProfileDetail");
      const [userDetails, setUserDetails] = useState<UpdateValues | null>(null);
      const [userType, setUserType] = useState<number>(0);
      const [commodityList, setCommodityList] = useState<CommodityType[]>([]);
      const { authenticatedCall: fetchCommodities } =
        actionsHandler("commodityList");
      const { getAuditChangesObject } = auditLoggingHandler();
      const [currentValues, setCurrentValues] = useState<any>(null);
      const [initiateCurrentValues, setInitiateCurrentValues] =
        useState<boolean>(false);
      const user = useSelector((state: RootState) => state.auth.user);
      const [masterProfiles, setMasterProfiles] = useState<MasterProfile[]>([]);
      const [isLoadingProfiles, setIsLoadingProfiles] = useState(false);
      const [otherDetails, setOtherDetails] = useState<any>({
        facility_required: false,
        facility_amount: 0,
        debtor_type: null,
        payment_terms: null,
        client_type: null,
        profession: null,
        reason_to_access: "",
        commodity_types: [],
      });

      useImperativeHandle(ref, () => ({
        formik: formik,
      }));

      const sendOTP = async (phone: string, email: string) => {
        return new Promise(async (resolve, reject) => {
          try {
            setIsLoading(true);
            const { authenticatedCall } = actionsHandler("sendOTP");
            const response = await dispatch(
              authenticatedCall({
                payload: {
                  phone,
                  email,
                },
                method: "post",
              }),
            );
            if (response.ok) {
              resolve(response.data);
            } else {
              reject(response.data);
            }
          } catch (error) {
            toast.error(t("auth:editMyAccount.toasts.error.unexpected"));
            reject(error);
          } finally {
            setIsLoading(false);
          }
        });
      };

      const handleOTPSuccess = async (payload: OTPPayload) => {
        try {
          await handleUpdateAccount(formik.values, payload);
        } catch (error) {
          toast.error(t("auth:editMyAccount.toasts.error.unexpected"));
        }
      };

      const formik = useFormik<UpdateValues>({
        initialValues: {
          phone: "",
          email: "",
          companies: [
            {
              id: 0,
              name: "",
              trading_as: "",
              commodity_types: [],
              administrative_first_name: "",
              administrative_last_name: "",
              administrative_email: "",
              administrative_contact_number: "",
              designation: "",
              facility_required: false,
              facility_amount: 0,
              debtor_type: {
                debtor_type: "",
                id: 0,
              },
              payment_terms: {
                payment_terms: "",
                id: 0,
              },
              client_type: {
                client_type: "",
                id: 0,
              },
              profession: 0,
              reason_to_access: "",
              administrative_profiles: [],
            },
          ],
          first_name: "",
          last_name: "",
        },
        validationSchema: Yup.object({
          phone: Yup.string()
            .required(t("auth:editMyAccount.validation.required.phone"))
            .min(9, t("auth:editMyAccount.validation.min.phone"))
            .max(10, t("auth:editMyAccount.validation.max.phone")),
          companies: Yup.array().of(
            Yup.object({
              name: Yup.string().required(
                t("auth:editMyAccount.validation.required.companyName"),
              ),
              trading_as: Yup.string().required(
                t("auth:editMyAccount.validation.required.tradingAs"),
              ),
              commodity_types: Yup.array().min(
                1,
                t("auth:editMyAccount.validation.required.commodityTypes"),
              ),
              administrative_first_name: Yup.string().required(
                t("auth:editMyAccount.validation.required.firstName"),
              ),
              administrative_last_name: Yup.string().required(
                t("auth:editMyAccount.validation.required.lastName"),
              ),
              administrative_email: Yup.string()
                .email(t("auth:editMyAccount.validation.invalid.email"))
                .required(t("auth:editMyAccount.validation.required.email")),
              administrative_contact_number: Yup.string()
                .required(
                  t("auth:editMyAccount.validation.required.contactNumber"),
                )
                .min(9, t("auth:editMyAccount.validation.min.contactNumber"))
                .max(10, t("auth:editMyAccount.validation.max.contactNumber")),
              designation: Yup.string().required(
                t("auth:editMyAccount.validation.required.designation"),
              ),
              administrative_profiles: Yup.array()
                .min(1, "At least one profile must be selected")
                .required("Profiles are required"),
            }),
          ),
        }),
        onSubmit: async (values) => {
          try {
            if (values.phone !== formik.initialValues.phone) {
              if (!isRequest) {
                const { authenticatedCall: validateEmailPhone } =
                  actionsHandler("validateEmailPhone");
                const validationResponse = await dispatch(
                  validateEmailPhone({
                    method: "post",
                    payload: {
                      email: values.companies[0].administrative_email,
                      phone: values.companies[0].administrative_contact_number,
                    },
                  }),
                );
                if (!validationResponse.ok) {
                  toast.error(validationResponse.data?.message);
                  return;
                } else {
                  const response: any = await sendOTP(
                    values.phone,
                    values.email,
                  );
                  dispatch(setTempEmail(values.email));
                  dispatch(setTempToken(response.token || ""));
                  dispatch(setOtpSent(true));
                  setShowOTPScreen(true);
                }
              }
            } else {
              toast.error(t("auth:editMyAccount.toasts.error.unexpected"));
            }
          } catch (error) {
            console.error("Error updating account details:", error);
            toast.error(t("auth:editMyAccount.messages.updateError"));
          }
        },
      });

      const getPayload = (values: UpdateValues) => {
        return {
          first_name: values.first_name || "",
          last_name: values.last_name || "",
          phone: values.phone || "",
          company_name: values.companies[0].name || "",
          trading_as: values.companies[0].trading_as || "",
          commodity_types: values.companies[0].commodity_types || [],
          administrative_first_name:
            values.companies[0].administrative_first_name || "",
          administrative_last_name:
            values.companies[0].administrative_last_name || "",
          administrative_email: values.companies[0].administrative_email || "",
          administrative_contact_number:
            values.companies[0].administrative_contact_number || "",
          designation: values.companies[0].designation || "",
          facility_required: values.companies[0].facility_required || false,
          facility_amount: values.companies[0].facility_amount || 1,
          debtor_type: otherDetails?.debtor_type?.id,
          payment_terms: otherDetails?.payment_terms?.id,
          client_type: otherDetails?.client_type?.id,
          profession: otherDetails?.profession?.id,
          reason_to_access: otherDetails?.reason_to_access || "-",
          administrative_profiles:
            values.companies[0].administrative_profiles.map(
              (profile: any) => profile,
            ) || [],
        };
      };

      const handleUpdateAccount = async (
        values: UpdateValues,
        otpPayload: OTPPayload,
      ) => {
        try {
          let payload = getPayload(values);
          const changes = getAuditChangesObject(currentValues, payload);
          console.log(changes, payload, currentValues);

          // If not a request, validate email and phone first

          // Proceed with account update if validation passed
          const { authenticatedCall: updateAccount } = actionsHandler(
            isRequest && user.user_type.id !== UserTierType.Tier3
              ? "updateAccountRequest"
              : "updateAccount",
          );

          const response = await dispatch(
            updateAccount({
              method:
                isRequest && user.user_type.id !== UserTierType.Tier3
                  ? "post"
                  : "patch",
              payload: {
                ...changes,
                otp: otpPayload.otp,
                otp_token: otpPayload.token,
              },
            }),
          );

          setShowOTPScreen(false);

          if (response.ok) {
            toast.success(
              response.data?.message ||
                t("auth:editMyAccount.toasts.success.update"),
            );
            if (customSubmitHandler) {
              customSubmitHandler();
            } else {
              navigate("/my-account");
            }
          }
        } catch (error) {
          console.error("Error updating account:", error);
        }
      };

      useEffect(() => {
        const fetchMasterProfiles = async () => {
          try {
            setIsLoadingProfiles(true);
            const { authenticatedCall } = actionsHandler("tierMasterProfiles");
            const response = await dispatch(
              authenticatedCall({
                method: "get",
              }),
            );

            if (response.ok) {
              setMasterProfiles(response.data);
            }
          } catch (error) {
            console.error("Error fetching master profiles:", error);
          } finally {
            setIsLoadingProfiles(false);
          }
        };

        fetchMasterProfiles();
      }, [dispatch]);

      useEffect(() => {
        const getUserDetails = async () => {
          try {
            setIsLoading(true);
            const response = await dispatch(
              fetchUserDetails({
                method: "get",
              }),
            );

            if (response.ok) {
              const userData = response.data;
              formik.setValues({
                first_name: userData.first_name || "",
                last_name: userData.last_name || "",
                phone: userData.phone || "",
                email: userData.email || "",
                companies: [
                  {
                    id: userData.companies?.[0]?.id,
                    name: userData.companies?.[0]?.name || "",
                    trading_as: userData.companies?.[0]?.trading_as || "",
                    commodity_types:
                      userData.companies?.[0]?.commodity_types.map(
                        (commodity: CommodityType) => commodity.id,
                      ) || [],
                    administrative_first_name:
                      userData.companies?.[0]?.administrative_first_name || "",
                    administrative_last_name:
                      userData.companies?.[0]?.administrative_last_name || "",
                    administrative_email:
                      userData.companies?.[0]?.administrative_email || "",
                    administrative_contact_number:
                      userData.companies?.[0]?.administrative_contact_number ||
                      "",
                    designation: userData.companies?.[0]?.designation || "",
                    facility_required:
                      userData.companies?.[0]?.facility_required ?? false,
                    facility_amount:
                      userData.companies?.[0]?.facility_amount || 0,
                    debtor_type: userData.companies?.[0]?.debtor_type || 0,
                    payment_terms: userData.companies?.[0]?.payment_terms || 0,
                    client_type: userData.companies?.[0]?.client_type || 0,
                    profession: userData.companies?.[0]?.profession || 0,
                    reason_to_access:
                      userData.companies?.[0]?.reason_to_access || "-",
                    custom_payment_days:
                      userData.companies?.[0]?.custom_payment_days || "",
                    credit_facility:
                      userData.companies?.[0]?.credit_facility || "",
                    administrative_profiles:
                      userData.companies?.[0]?.administrative_profiles.map(
                        (profile: MasterProfile) => profile.id,
                      ) || [],
                  },
                ],
              });
              setOtherDetails({
                facility_required:
                  userData.companies?.[0]?.facility_required ?? false,
                facility_amount: userData.companies?.[0]?.facility_amount || 0,
                debtor_type: userData.companies?.[0]?.debtor_type || null,
                payment_terms: userData.companies?.[0]?.payment_terms || null,
                client_type: userData.companies?.[0]?.client_type || null,
                profession: userData.companies?.[0]?.profession || null,
                reason_to_access:
                  userData.companies?.[0]?.reason_to_access || "",
              });

              setUserType(userData.user_type.id);
              setInitiateCurrentValues(true);
            }
          } catch (error) {
            console.error("Error fetching user details:", error);
          } finally {
            setIsLoading(false);
          }
        };

        if (userId) {
          getUserDetails();
        }
      }, [dispatch, userId]);

      useEffect(() => {
        const getCommodityList = async () => {
          try {
            const response = await dispatch(
              fetchCommodities({
                method: "get",
              }),
            );
            if (response.ok) {
              setCommodityList(response.data);
            }
          } catch (error) {
            console.error("Error fetching commodities:", error);
          }
        };

        getCommodityList();
      }, [dispatch]);

      useEffect(() => {
        if (initiateCurrentValues) {
          setCurrentValues(getPayload(formik.values));
          userDataAvailableCallback?.();
        }
      }, [initiateCurrentValues]);

      if (isLoading) {
        return (
          <div className="tw-flex tw-justify-center tw-items-center tw-h-[60vh]">
            <CustomLoader loading={true} isWrapper={true} size="50" />
          </div>
        );
      }

      const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        e.stopPropagation();
        const values = formik.values;
        try {
          if (values.phone !== formik.initialValues.phone) {
            if (!isRequest) {
              const { authenticatedCall: validateEmailPhone } =
                actionsHandler("validateEmailPhone");
              const validationResponse = await dispatch(
                validateEmailPhone({
                  method: "post",
                  payload: {
                    email: values.companies[0].administrative_email,
                    phone: values.companies[0].administrative_contact_number,
                  },
                }),
              );
              if (!validationResponse.ok) {
                toast.error(validationResponse.data?.message);
                return;
              } else {
                const response: any = await sendOTP(values.phone, values.email);
                dispatch(setTempEmail(values.email));
                dispatch(setTempToken(response.token || ""));
                dispatch(setOtpSent(true));
                setShowOTPScreen(true);
              }
            } else {
              const response: any = await sendOTP(values.phone, values.email);
              dispatch(setTempEmail(values.email));
              dispatch(setTempToken(response.token || ""));
              dispatch(setOtpSent(true));
              setShowOTPScreen(true);
            }
          }
        } catch (error) {
          console.error("Error updating account details:", error);
        }
      };
      return (
        <>
          {showOTPScreen ? (
            <PhoneVerification
              successCallback={handleOTPSuccess}
              isDialog={true}
              backButton={true}
              onBack={() => setShowOTPScreen(false)}
            />
          ) : (
            <>
              {/* Header Section */}
              {!hideDefaultHeader && (
                <>
                  <div className="tw-fixed tw-h-[68px] tw-top-[80px] tw-w-full tw-z-10">
                    <div className="tw-px-8 tw-py-4 tw-bg-white tw-border-b border-[#E9EAEB]">
                      <div className="tw-flex tw-justify-between tw-items-center">
                        <div className="tw-flex tw-items-center">
                          <a href="/my-account">
                            <img src={BackIcon} alt="Back Icon" />{" "}
                          </a>
                          <h1 className="tw-text-xl tw-ml-4 tw-font-semibold font-primary-color">
                            {t("auth:editMyAccount.title")}
                          </h1>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )}
              <form
                onSubmit={handleSubmit}
                className={`tw-bg-gray-50 tw-font-sans ${className}`}
                noValidate
              >
                {/* Content Section */}
                <div
                  className={`tw-py-4 ${!hideDefaultHeader ? "tw-space-y-6 tw-px-8 tw-mt-[140px]" : "tw-space-y-4"}`}
                >
                  <div className="tw-bg-white custom-card">
                    {/* My Account Details */}
                    <div>
                      <div className="custom-card-header">
                        <h3 className="font-size-base tw-font-semibold font-primary-color">
                          Primary Details
                        </h3>
                      </div>
                      <div className="custom-card-body">
                        <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2">
                          <div className="form-group tw-mb-6">
                            <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                              First Name
                            </p>
                            <TextField
                              fullWidth
                              type="text"
                              name="first_name"
                              variant="outlined"
                              placeholder="Enter First Name"
                              className="tw-mb-4"
                              value={formik.values.first_name}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.first_name &&
                                Boolean(formik.errors.first_name)
                              }
                              helperText={
                                formik.touched.first_name &&
                                formik.errors.first_name
                              }
                            />
                          </div>
                          <div className="form-group tw-mb-6">
                            <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                              Last Name
                            </p>
                            <TextField
                              fullWidth
                              type="text"
                              name="last_name"
                              variant="outlined"
                              placeholder="Enter Last Name"
                              className="tw-mb-4"
                              value={formik.values.last_name}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.last_name &&
                                Boolean(formik.errors.last_name)
                              }
                              helperText={
                                formik.touched.last_name &&
                                formik.errors.last_name
                              }
                            />
                          </div>
                          <div className="form-group tw-mb-6">
                            <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                              Email
                            </p>
                            <TextField
                              fullWidth
                              type="email"
                              name="email"
                              variant="outlined"
                              placeholder="Enter Email"
                              className="tw-mb-4"
                              value={formik.values.email}
                              InputProps={{
                                readOnly: true,
                              }}
                              disabled
                              sx={{
                                "& .Mui-disabled": {
                                  backgroundColor: "#f5f5f5",
                                  WebkitTextFillColor: "#666666",
                                },
                              }}
                            />
                          </div>
                          <div className="form-group tw-mb-6">
                            <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                              Contact Number
                            </p>
                            <TextField
                              fullWidth
                              type="text"
                              name="phone"
                              variant="outlined"
                              placeholder="Enter Contact Number"
                              className="tw-mb-4"
                              value={formik.values.phone}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              error={
                                formik.touched.phone &&
                                Boolean(formik.errors.phone)
                              }
                              helperText={
                                formik.touched.phone && formik.errors.phone
                              }
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="tw-bg-white custom-card">
                    {/* My Account Details */}
                    <div>
                      <div className="custom-card-header">
                        <h3 className="font-size-base tw-font-semibold font-primary-color">
                          Company Details
                        </h3>
                      </div>
                      <div className="custom-card-body">
                        {formik.values.companies?.length > 0 &&
                          formik.values.companies[0] && (
                            <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2">
                              <div className="form-group tw-mb-6">
                                <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                                  Registered Company Name
                                </p>
                                <TextField
                                  fullWidth
                                  type="text"
                                  name="companies.0.name"
                                  variant="outlined"
                                  placeholder="Enter Company Name"
                                  className="tw-mb-4"
                                  value={formik.values.companies[0].name}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={
                                    isRequest &&
                                    Boolean(formik.values.companies[0].name)
                                  }
                                  error={
                                    formik.touched.companies?.[0]?.name &&
                                    Boolean(
                                      (
                                        formik.errors
                                          .companies?.[0] as FormikErrors<CompanyDetails>
                                      )?.name,
                                    )
                                  }
                                  helperText={
                                    formik.touched.companies?.[0]?.name &&
                                    (
                                      formik.errors
                                        .companies?.[0] as FormikErrors<CompanyDetails>
                                    )?.name
                                  }
                                />
                              </div>
                              <div className="form-group tw-mb-6">
                                <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                                  Trading As
                                </p>
                                <TextField
                                  fullWidth
                                  type="text"
                                  name="companies.0.trading_as"
                                  variant="outlined"
                                  placeholder="Enter Trading As"
                                  className="tw-mb-4"
                                  value={formik.values.companies[0].trading_as}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={
                                    isRequest &&
                                    Boolean(
                                      formik.values.companies[0].trading_as,
                                    )
                                  }
                                  error={
                                    formik.touched.companies?.[0]?.trading_as &&
                                    Boolean(
                                      (
                                        formik.errors
                                          .companies?.[0] as FormikErrors<CompanyDetails>
                                      )?.trading_as,
                                    )
                                  }
                                  helperText={
                                    formik.touched.companies?.[0]?.trading_as &&
                                    (
                                      formik.errors
                                        .companies?.[0] as FormikErrors<CompanyDetails>
                                    )?.trading_as
                                  }
                                />
                              </div>
                              <div className="form-group tw-mb-6">
                                <DynamicAutocomplete
                                  name="companies.0.commodity_types"
                                  label="Commodity Type"
                                  multiple
                                  maxChips={2}
                                  maxLengthChip={12}
                                  filterOptions={(options, { inputValue }) =>
                                    options.filter((option) =>
                                      option.label
                                        ?.toLowerCase()
                                        .includes(
                                          inputValue?.toLowerCase() || "",
                                        ),
                                    )
                                  }
                                  options={commodityList}
                                  selectedValue={
                                    formik.values.companies[0].commodity_types
                                  }
                                  labelKey="title"
                                  valueKey="id"
                                  onChange={(value: any) => {
                                    formik.setFieldValue(
                                      "companies.0.commodity_types",
                                      value,
                                    );
                                  }}
                                  touched={Boolean(
                                    formik.touched.companies?.[0]
                                      ?.commodity_types,
                                  )}
                                  error={
                                    typeof formik.errors.companies?.[0] ===
                                    "object"
                                      ? String(
                                          (
                                            formik.errors
                                              .companies[0] as FormikErrors<CompanyDetails>
                                          )?.commodity_types,
                                        )
                                      : ""
                                  }
                                />
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                  <div className="tw-bg-white custom-card">
                    {/* My Account Details */}
                    <div>
                      <div className="custom-card-header">
                        <h3 className="font-size-base tw-font-semibold font-primary-color">
                          Administrative Contact Details
                        </h3>
                      </div>
                      <div className="custom-card-body">
                        {formik.values.companies?.length > 0 &&
                          formik.values.companies[0] && (
                            <div className="tw-grid lg:tw-grid-cols-3 md:tw-grid-cols-2 lg:tw-gap-6 md:tw-gap-4 tw-gap-2">
                              <div className="form-group tw-mb-6">
                                <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                                  First Name
                                </p>
                                <TextField
                                  fullWidth
                                  type="text"
                                  name="companies.0.administrative_first_name"
                                  variant="outlined"
                                  placeholder="Enter First Name"
                                  className="tw-mb-4"
                                  value={
                                    formik.values.companies?.[0]
                                      ?.administrative_first_name ?? ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={
                                    isRequest &&
                                    Boolean(
                                      formik.values.companies[0]
                                        .administrative_first_name,
                                    )
                                  }
                                  error={
                                    formik.touched.companies?.[0]
                                      ?.administrative_first_name &&
                                    Boolean(
                                      (
                                        formik.errors
                                          .companies?.[0] as FormikErrors<CompanyDetails>
                                      )?.administrative_first_name,
                                    )
                                  }
                                  helperText={
                                    formik.touched.companies?.[0]
                                      ?.administrative_first_name &&
                                    (
                                      formik.errors
                                        .companies?.[0] as FormikErrors<CompanyDetails>
                                    )?.administrative_first_name
                                  }
                                />
                              </div>
                              <div className="form-group tw-mb-6">
                                <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                                  Last Name
                                </p>
                                <TextField
                                  fullWidth
                                  type="text"
                                  name="companies.0.administrative_last_name"
                                  variant="outlined"
                                  placeholder="Enter Last Name"
                                  className="tw-mb-4"
                                  value={
                                    formik.values.companies?.[0]
                                      ?.administrative_last_name ?? ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={
                                    isRequest &&
                                    Boolean(
                                      formik.values.companies[0]
                                        .administrative_last_name,
                                    )
                                  }
                                  error={
                                    formik.touched.companies?.[0]
                                      ?.administrative_last_name &&
                                    Boolean(
                                      (
                                        formik.errors
                                          .companies?.[0] as FormikErrors<CompanyDetails>
                                      )?.administrative_last_name,
                                    )
                                  }
                                  helperText={
                                    formik.touched.companies?.[0]
                                      ?.administrative_last_name &&
                                    (
                                      formik.errors
                                        .companies?.[0] as FormikErrors<CompanyDetails>
                                    )?.administrative_last_name
                                  }
                                />
                              </div>
                              <div className="form-group tw-mb-6">
                                <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                                  Primary Email
                                </p>
                                <TextField
                                  fullWidth
                                  type="email"
                                  name="companies.0.administrative_email"
                                  variant="outlined"
                                  placeholder="Enter Primary Email"
                                  className="tw-mb-4"
                                  value={
                                    formik.values.companies?.[0]
                                      ?.administrative_email ?? ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={
                                    isRequest &&
                                    Boolean(
                                      formik.values.companies[0]
                                        .administrative_email,
                                    )
                                  }
                                  error={
                                    formik.touched.companies?.[0]
                                      ?.administrative_email &&
                                    Boolean(
                                      (
                                        formik.errors
                                          .companies?.[0] as FormikErrors<CompanyDetails>
                                      )?.administrative_email,
                                    )
                                  }
                                  helperText={
                                    formik.touched.companies?.[0]
                                      ?.administrative_email &&
                                    (
                                      formik.errors
                                        .companies?.[0] as FormikErrors<CompanyDetails>
                                    )?.administrative_email
                                  }
                                />
                              </div>
                              <div className="form-group tw-mb-6">
                                <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                                  Primary Contact Number
                                </p>
                                <TextField
                                  fullWidth
                                  type="number"
                                  name="companies.0.administrative_contact_number"
                                  variant="outlined"
                                  placeholder="Enter Primary Contact Number"
                                  className="tw-mb-4"
                                  value={
                                    formik.values.companies?.[0]
                                      ?.administrative_contact_number ?? ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={
                                    isRequest &&
                                    Boolean(
                                      formik.values.companies[0]
                                        .administrative_contact_number,
                                    )
                                  }
                                  error={
                                    formik.touched.companies?.[0]
                                      ?.administrative_contact_number &&
                                    Boolean(
                                      (
                                        formik.errors
                                          .companies?.[0] as FormikErrors<CompanyDetails>
                                      )?.administrative_contact_number,
                                    )
                                  }
                                  helperText={
                                    formik.touched.companies?.[0]
                                      ?.administrative_contact_number &&
                                    (
                                      formik.errors
                                        .companies?.[0] as FormikErrors<CompanyDetails>
                                    )?.administrative_contact_number
                                  }
                                />
                              </div>
                              <div className="form-group tw-mb-6">
                                <p className="font-gray-color-dark font-weight-medium font-size-sm leading tw-mb-2">
                                  Role/Designation
                                </p>
                                <TextField
                                  fullWidth
                                  type="text"
                                  name="companies.0.designation"
                                  variant="outlined"
                                  placeholder="Enter Role/Designation"
                                  className="tw-mb-4"
                                  value={
                                    formik.values.companies?.[0]?.designation ??
                                    ""
                                  }
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  disabled={
                                    isRequest &&
                                    Boolean(
                                      formik.values.companies[0].designation,
                                    )
                                  }
                                  error={
                                    formik.touched.companies?.[0]
                                      ?.designation &&
                                    Boolean(
                                      (
                                        formik.errors
                                          .companies?.[0] as FormikErrors<CompanyDetails>
                                      )?.designation,
                                    )
                                  }
                                  helperText={
                                    formik.touched.companies?.[0]
                                      ?.designation &&
                                    (
                                      formik.errors
                                        .companies?.[0] as FormikErrors<CompanyDetails>
                                    )?.designation
                                  }
                                />
                              </div>
                              <div className="form-group tw-mb-6">
                                <DynamicAutocomplete
                                  name="companies.0.administrative_profiles"
                                  label="Master Profiles"
                                  options={masterProfiles}
                                  selectedValue={
                                    formik.values.companies?.[0]?.administrative_profiles.map(
                                      (profile) => profile,
                                    ) || []
                                  }
                                  labelKey="name"
                                  valueKey="id"
                                  onChange={(newValue: any) => {
                                    formik.setFieldValue(
                                      "companies.0.administrative_profiles",
                                      newValue,
                                    );
                                  }}
                                  multiple
                                  maxChips={2}
                                  placeholder="Select master profiles"
                                  filterOptions={(options, { inputValue }) =>
                                    options.filter((option) =>
                                      option.label
                                        ?.toLowerCase()
                                        .includes(
                                          inputValue?.toLowerCase() || "",
                                        ),
                                    )
                                  }
                                  error={
                                    formik.touched.companies?.[0]
                                      ?.administrative_profiles
                                      ? Array.isArray(
                                          (
                                            formik.errors
                                              .companies?.[0] as FormikErrors<CompanyDetails>
                                          )?.administrative_profiles,
                                        )
                                        ? (
                                            (
                                              formik.errors
                                                .companies?.[0] as FormikErrors<CompanyDetails>
                                            )
                                              ?.administrative_profiles as string[]
                                          )?.join(", ")
                                        : ((
                                            formik.errors
                                              .companies?.[0] as FormikErrors<CompanyDetails>
                                          )?.administrative_profiles as string)
                                      : undefined
                                  }
                                  touched={Boolean(
                                    formik.touched.companies?.[0]
                                      ?.administrative_profiles,
                                  )}
                                  loading={isLoadingProfiles}
                                />
                              </div>
                            </div>
                          )}
                      </div>
                    </div>
                  </div>
                  {[
                    UserTierType.Tier4,
                    UserTierType.Tier5,
                    UserTierType.Tier6,
                    UserTierType.Tier7,
                  ].includes(userType) && (
                    <div className="tw-bg-white custom-card">
                      <div>
                        <div className="custom-card-header">
                          <h3 className="font-size-base tw-font-semibold font-primary-color">
                            Other Details
                          </h3>
                        </div>
                        <div className="custom-card-body">
                          <RenderOtherDetails
                            userType={userType}
                            formik={formik}
                            otherDetails={otherDetails}
                            onOtherDetailsChange={(newDetails) => {
                              setOtherDetails(newDetails);
                              formik.setValues({
                                ...formik.values,
                                companies: [
                                  {
                                    ...formik.values.companies[0],
                                    ...newDetails,
                                  },
                                ],
                              });
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {!hideSubmitButton && (
                    <div className="tw-flex tw-justify-center tw-items-center">
                      <button
                        type="submit"
                        className="tw-bg-blue-600 hover:tw-bg-blue-700 tw-text-white tw-rounded-md tw-px-4 tw-py-2 primary-custom-button"
                        disabled={formik.isSubmitting}
                      >
                        {t("auth:editMyAccount.buttons.save")}
                      </button>
                    </div>
                  )}
                </div>
              </form>
            </>
          )}
        </>
      );
    },
  ),
);

export default EditMyAccount;
